import React, { useState } from 'react';
import { BiHide, BiShow } from 'react-icons/bi';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ChangePassword } from '../Features/Changepassword';
import { RootState, AppDispatch } from '../Appstore/store';
import { toast } from "sonner";


interface ChangePasswordValues {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const Changepass: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const location = useLocation();
    const isActive = (path: string) => location.pathname === path ? 'active' : '';

    const initialValues: ChangePasswordValues = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    };


    const validationSchema = Yup.object({
        oldPassword: Yup.string()
            .required('Old password is required'),
        newPassword: Yup.string()
            .required('New password is required')
            .min(8, 'New password must be at least 8 characters')
            .matches(/[a-z]/, "New password must contain at least one lowercase letter")
            .matches(/[A-Z]/, "New password must contain at least one uppercase letter")
            .matches(/\d/, "Password must contain at least one number"),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('newPassword')], 'Passwords must match')
            .required('Confirm password is required')
    });

    const handleSubmit = async (values: ChangePasswordValues, { setSubmitting, resetForm }: FormikHelpers<ChangePasswordValues>) => {
        try {
            const response: any = await dispatch(
                ChangePassword(values)
            );
            if (
                response.payload &&
                response.payload.status === "success"
            ) {
                resetForm()
                toast.success(response.payload.message);
            } else {
                toast.error(response.payload.message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="wrapper">
                <div className="content-wrapper">
                    <section className="content">
                        <div className='container-fluid'>
                            <div className='card mt-4'>
                                <div className="card-header bg-white p-3 d-md-flex justify-content-md-between align-items-center ">
                                    <h2 className="w-100 fs-3">Change Password</h2>
                                </div>

                                <div className="card-body">
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        onSubmit={handleSubmit}
                                    >
                                        {({ isSubmitting, errors, touched }) => (
                                            <Form>
                                                <div className='col-md-6'>
                                                    <div className='mb-3'>
                                                        <div className="mb-0">
                                                            <label htmlFor="oldPassword">Old Password<span className="text-danger">*</span></label>
                                                            <div className='position-relative'>
                                                                <Field
                                                                    type={showOldPassword ? 'text' : 'password'}
                                                                    name="oldPassword"
                                                                    className={`form-control shadow-none ${touched.oldPassword && errors.oldPassword ? 'border border-danger' : ''}`}
                                                                    id="oldPassword"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-light position-absolute bg-white "
                                                                    onClick={() => setShowOldPassword(!showOldPassword)}
                                                                    style={{
                                                                        top: '50%',
                                                                        right: '1px',
                                                                        transform: 'translateY(-50%)',
                                                                        border: 'none',
                                                                        background: 'none',
                                                                    }}
                                                                >
                                                                    {showOldPassword ? <BiShow /> : <BiHide />}
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {touched.oldPassword && errors.oldPassword && (
                                                            <div className="text-danger">
                                                                {Array.isArray(errors.oldPassword) ? (
                                                                    errors.oldPassword.map((err, idx) => <div key={idx}>{err}</div>)
                                                                ) : (
                                                                    <div>{errors.oldPassword}</div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className='mb-3'>
                                                        <div>
                                                            <label htmlFor="newPassword">New Password<span className="text-danger">*</span></label>
                                                            <div className='position-relative'>
                                                                <Field
                                                                    type={showNewPassword ? 'text' : 'password'}
                                                                    name="newPassword"
                                                                    className={`form-control shadow-none ${touched.newPassword && errors.newPassword ? 'border border-danger' : ''}`}
                                                                    id="newPassword"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-light position-absolute bg-white "
                                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                                    style={{
                                                                        top: '50%',
                                                                        right: '1px',
                                                                        transform: 'translateY(-50%)',
                                                                        border: 'none',
                                                                        background: 'none',
                                                                    }}
                                                                >
                                                                    {showNewPassword ? <BiShow /> : <BiHide />}
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {touched.newPassword && errors.newPassword && (
                                                            <div className="text-danger">
                                                                {Array.isArray(errors.newPassword) ? (
                                                                    errors.newPassword.map((err, idx) => <div key={idx}>{err}</div>)
                                                                ) : (
                                                                    <div>{errors.newPassword}</div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className='mb-3'>
                                                        <div >
                                                            <label htmlFor="confirmPassword">Confirm New Password<span className="text-danger">*</span></label>
                                                            <div className="position-relative">
                                                                <Field
                                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                                    name="confirmPassword"
                                                                    className={`form-control shadow-none ${touched.confirmPassword && errors.confirmPassword ? 'border border-danger' : ''}`}
                                                                    id="confirmPassword"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-light position-absolute bg-white "
                                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                                    style={{
                                                                        top: '50%',
                                                                        right: '1px',
                                                                        transform: 'translateY(-50%)',
                                                                        border: 'none',
                                                                        background: 'none',
                                                                    }}
                                                                >
                                                                    {showConfirmPassword ? <BiShow /> : <BiHide />}
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {touched.confirmPassword && errors.confirmPassword && (
                                                            <div className="text-danger">
                                                                {Array.isArray(errors.confirmPassword) ? (
                                                                    errors.confirmPassword.map((err, idx) => <div key={idx}>{err}</div>)
                                                                ) : (
                                                                    <div>{errors.confirmPassword}</div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <div className="d-flex">
                                                            <button type="submit" className="btn btn-primary me-2" disabled={isSubmitting}>
                                                                {isSubmitting ? (
                                                                    <>
                                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                        <span className='ps-2'>Submitting...</span>
                                                                    </>
                                                                ) : (
                                                                    "Submit"
                                                                )}
                                                            </button>
                                                            <Link to="/admin/dashboard" className={`nav-link ${isActive('/admin/dashboard')}`}>
                                                                <button className='btn btn-secondary'>Cancel</button>
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </section>
                </div >
            </div >
        </>
    );
}

export default Changepass;
